import isFalsy from '../helpers/is-falsy';
import isTruthy from '../helpers/is-truthy';
import { parseDate } from '../helpers/parse-date';

const projectLedgers = {
  'Import ID': {
    requirementType: 'required',
    description:
      'Unique identifier for the imported record. Imported row will be checked for duplicate. If a duplicate is found, the row will be skipped from import.',
    dataType: 'string',
    validator: (value) => {
      return value !== undefined && value !== null && value.trim() !== '';
    },
  },
  'Client Name': {
    requirementType: 'required',
    description:
      'The Name of the client associated with this ledger entry. This field must match the Name of a Client exactly, including spaces, punctuation and capitalization, otherwise the ledger entry may be skipped during import.',
    dataType: 'string',
    validator: (value) => value.trim() !== '',
  },
  'Project Name': {
    requirementType: 'required',
    description:
      'The name of the project associated with the ledger entry. This field must match the name of a project exactly, including spaces, punctuation and capitalization, otherwise the row may be skipped during import.',
    dataType: 'string',
    validator: (value) => value.trim() !== '',
  },
  Ledger: {
    requirementType: 'required',
    description: `Indicate the project ledger on which the entry should be created: 'expense' for Project Expenses, 'product' for Products, 'service' for Additional Services, 'other' for Other Items, 'milestone' for the Fixed Fee Billing Schedule, and 'revenue' for the Revenue Recognition Ledger.`,
    dataType: 'string',
    validator: (value) =>
      ['milestone', 'expense', 'product', 'service', 'other', 'revenue'].includes(value.trim().toLowerCase()),
  },
  Notes: {
    requirementType: 'conditional',
    description: 'Notes is required for "milestone", "product", "service", and "other" ledger items.',
    dataType: 'string',
    validator: (value, row) => {
      const ledger = row['Ledger']?.trim().toLowerCase();
      const r = ['milestone', 'product', 'service', 'other'].includes(ledger) && !value?.trim() ? false : true;
      return r;
    },
  },
  Date: {
    requirementType: 'required',
    description: 'Date of item being imported in "YYYY-MM-DD" or "MM/DD/YYYY" format.',
    dataType: 'date',
    validator: (value) => {
      return !!parseDate(value);
    },
  },
  Amount: {
    requirementType: 'required',
    description:
      'Amount of the ledger item; project currency will be assumed. Quantity and rate, if provided, will be used to check Amount is valid.',
    dataType: 'number',
    validator: (value, row) => {
      const isValidAmount =
        value !== undefined && value !== null && value !== '' && !isNaN(parseFloat(value)) && isFinite(value);
      const isValidQuantity =
        row['Quantity'] !== undefined &&
        row['Quantity'] !== '' &&
        !isNaN(parseFloat(row['Quantity'])) &&
        isFinite(row['Quantity']) &&
        Number(row['Quantity']) > 0;
      const isValidRate =
        row['Rate'] !== undefined && row['Rate'] !== '' && !isNaN(parseFloat(row['Rate'])) && isFinite(row['Rate']);

      const matchesQuantityAndRate =
        isValidQuantity && isValidRate ? Number(value) === Number(row['Quantity']) * Number(row['Rate']) : true;

      return isValidAmount && matchesQuantityAndRate;
    },
  },
  Quantity: {
    requirementType: 'conditional',
    description:
      'Quantity of the ledger item; this will only be used for Fixed Fee Billing Schedule, Products, Additional Services, and Other Items.',
    dataType: 'number',
    validator: (value, row) => {
      const ledger = row['Ledger']?.trim().toLowerCase();
      const r =
        !['product', 'service', 'other'].includes(ledger) ||
        value === undefined ||
        value === '' ||
        (!isNaN(parseFloat(value)) && isFinite(value) && Number(value) > 0);
      return r;
    },
  },
  Rate: {
    requirementType: 'conditional',
    description:
      'Rate of the ledger item; this will only be used for Fixed Fee Billing Schedule, Products, Additional Services, and Other Items.',
    dataType: 'number',
    validator: (value, row) => {
      const ledger = row['Ledger']?.trim().toLowerCase();
      const hasQuantity = row['Quantity'] !== undefined && row['Quantity'] !== '';
      const r =
        !['product', 'service', 'other'].includes(ledger) ||
        (!hasQuantity && (value === undefined || value === '')) ||
        (!isNaN(parseFloat(value)) && isFinite(value));
      return r;
    },
  },
  'Invoice Item': {
    requirementType: 'conditional',
    description:
      'Invoice Item of the ledger item; required if ledger is "milestone", "product", "service", or "other". Specify the Invoice Item that you wish to be associated with the item being created on the ledger. If not found the default will be automatically assigned.',
    dataType: 'string',
    helpUrl:
      'https://help.ruddr.io/faqs-and-troubleshooting/billing/how-do-default-invoice-items-get-applied-in-invoices',
    validator: (value, row) => {
      const ledger = row['Ledger']?.trim().toLowerCase();
      const r =
        !['milestone', 'product', 'service', 'other'].includes(ledger) ||
        (['milestone', 'product', 'service', 'other'].includes(ledger) && !!value?.trim());
      return r;
    },
  },
  'Expense Category': {
    requirementType: 'conditional',
    description: 'Expense Category of the ledger item, required if ledger is "expense".',
    dataType: 'string',
    helpUrl: 'https://help.ruddr.io/expenses/manage-expense-categories',
    validator: (value, row) => {
      const r = row['Ledger'] != 'expense' || (row['Ledger'] === 'expense' && value.trim() !== '');
      return r;
    },
  },
  Billable: {
    requirementType: 'optional',
    description: '"Yes" if you consider this item billable to the client; only used if ledger is "expense".',
    dataType: 'boolean',
    validator: (value, row) => {
      return (
        row['Ledger'] !== 'expense' ||
        (row['Ledger'] === 'expense' && (value === undefined || isTruthy(value) || isFalsy(value)))
      );
    },
  },
  Vendor: {
    requirementType: 'optional',
    description: 'Vendor related to the ledger item, only is used if ledger is "expense".',
    dataType: 'string',
    validator: () => true,
  },
};

export default projectLedgers;
